import { useCallback, useEffect, useRef, useState } from 'react';
import { StyleValues } from '../../types';
import { SpacingDataResult } from '../types';
import { decompose } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import { compose } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/composer';
import { updateValueWithPercentage } from '../utils';
import { PERCENTAGE_START_POINT } from '../constants';

export const useSpacingControl = (data: SpacingDataResult | null) => {
  const [sharedValue, setSharedValue] = useState<string | null>(
    data?.sharedValue || null,
  );
  const [percentageValue, setPercentageValue] = useState<number>(
    PERCENTAGE_START_POINT,
  );

  useEffect(() => {
    setSharedValue(data?.sharedValue || null);
  }, [data?.sharedValue]);

  const isControlVisible = Boolean(data?.sharedValue) || Boolean(data?.isMixed);

  const setLocalValue = (value: string) => {
    if (data?.isMixed) {
      setPercentageValue(parseInt(value, 10));
    } else {
      setSharedValue(value);
    }
  };

  const setValues = (value: string) => {
    if (!data) {
      return;
    }

    data.propertiesMap.forEach((clientData, client) => {
      const stylesChanges: StyleValues = {
        cssProperties: {},
        cssCustomProperties: {},
      };

      if (data.isMixed) {
        const percentage = parseInt(value, 10);

        clientData.forEach((propertyData) => {
          const decomposed = decompose(
            propertyData.propertyType,
            propertyData.originalValue,
          );

          if (decomposed) {
            Object.entries(decomposed).forEach(
              ([decomposedKey, decomposedValue]) => {
                if (!decomposedValue) {
                  return;
                }

                decomposed[decomposedKey] = updateValueWithPercentage(
                  decomposedValue,
                  percentage,
                );
              },
            );

            stylesChanges[propertyData.namespace][propertyData.styleItemId] =
              compose(propertyData.propertyType, decomposed)!;
          } else {
            stylesChanges[propertyData.namespace][propertyData.styleItemId] =
              updateValueWithPercentage(propertyData.originalValue, percentage);
          }
        });
      } else {
        clientData.forEach(
          (propertyData) =>
            (stylesChanges[propertyData.namespace][propertyData.styleItemId] =
              value),
        );
      }

      if (
        Object.keys(stylesChanges.cssProperties).length ||
        Object.keys(stylesChanges.cssCustomProperties).length
      ) {
        client.element.setStyles(stylesChanges);
      }
    });
  };

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onChange = (value: string) => {
    setLocalValue(value);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setValues(value);
    }, 300);
  };

  return {
    sharedValue,
    percentageValue,
    isMixed: Boolean(data?.isMixed),
    onChange,
    isControlVisible,
  };
};
